import { useRef, useState } from "react";

type Props = {
  before: string;
  after: string;
  label: string;
  aspectClass?: string;
};

export function BeforeAfter({ before, after, label, aspectClass = "aspect-4/3" }: Props) {
  const [pos, setPos] = useState(50);
  const ref = useRef<HTMLDivElement>(null);

  const move = (clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, next)));
  };

  return (
    <figure className="group">
      <div
        ref={ref}
        className={`relative ${aspectClass} w-full touch-none cursor-ew-resize overflow-hidden rounded-sm bg-muted select-none`}
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          move(e.clientX);
        }}
        onPointerMove={(e) => {
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
          e.preventDefault();
          move(e.clientX);
        }}
        onPointerUp={(e) => e.currentTarget.releasePointerCapture(e.pointerId)}
        onDragStart={(e) => e.preventDefault()}
      >
        <img
          src={after}
          alt={`${label} — dopo l'intervento`}
          loading="lazy"
          draggable={false}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="pointer-events-none absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${pos}%` }}
        >
          <img
            src={before}
            alt={`${label} — prima dell'intervento`}
            loading="lazy"
            draggable={false}
            className="absolute inset-0 h-full max-w-none object-cover"
            style={{ width: pos > 0 ? `${10000 / pos}%` : "100%" }}
          />
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 w-px bg-background/90"
          style={{ left: `${pos}%` }}
        >
          <span className="absolute top-1/2 left-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-background text-sienna shadow-[var(--shadow-soft)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 6 3 12l6 6M15 6l6 6-6 6" />
            </svg>
          </span>
        </div>
        <span className="pointer-events-none absolute bottom-3 left-3 rounded-xs bg-ink/70 px-2 py-1 text-[0.65rem] font-semibold tracking-[0.18em] text-background uppercase">
          Prima
        </span>
        <span className="pointer-events-none absolute right-3 bottom-3 rounded-xs bg-primary/85 px-2 py-1 text-[0.65rem] font-semibold tracking-[0.18em] text-primary-foreground uppercase">
          Dopo
        </span>
      </div>
      <figcaption className="mt-3 text-sm text-muted-foreground">{label}</figcaption>
    </figure>
  );
}
